/**
 * Main Application Entry Point (stable path)
 */
import './styles/main-v541.css'; /* Organized v5.4.1 */
import './styles/causality.css'; /* Canvas + side-panels for Business Levers */
import './styles/strategy.css';
import './styles/settings.css';
/* Fix styles now integrated into main-v501.css (v5.4.1) */
import EventBus, { AppEvents } from './core/EventBus.js';
import Config from './core/Config.js';
import App from './modules/App.js';
import actionRouter from './core/ActionRouter.js';
import * as ActionRegistry from './core/ActionRegistry.js';
// Register core services into the DI container early
import './services/registry.js';
import { ModalService } from './ui-kit/ModalService.jsx';
import { LegacyAdapter } from './legacy/LegacyAdapter.js';
import ApiService from './services/ApiService.js';
import ControlPlane from './services/ControlPlaneService.js';
import PromptService from './services/PromptService.js';
import './utils/globalErrorHandler.js';
import './store/portfolioBridge.js';
import { bindPromptEditors } from './components/PromptBinder.js';

async function initializeApp() {
  console.log(`Strategic Intelligence Platform v${Config.APP_VERSION} (${Config.APP_ENV})`);
  try {
    // Optional: Cognito login gate for pilot environments
    try {
      if (import.meta.env?.VITE_ENABLE_COGNITO === 'true') {
        const { default: Auth } = await import('./services/AuthServiceCognito.js');
        const ok = await Auth.ensureAuthenticated();
        if (!ok) return; // redirected to login
      }
    } catch(_) {}

    // Mark environment on <html> and load styles conditionally
    try {
      const env = Config.APP_ENV || (import.meta.env?.DEV ? 'development' : 'production');
      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-env', env);
      }
      // DEV theme: include only when building for DEV, or when pilot flag is set at build time
      if (import.meta.env.DEV) {
        await import('./styles/dev-53-theme.css');
      } else if (import.meta.env?.VITE_LOAD_DEV_THEME === 'true') {
        await import('./styles/dev-53-theme.css');
      }
      // Enterprise theme may be enabled in both DEV and PROD via feature flag
      try { if (Config.FEATURES?.THEME_ENTERPRISE === true) await import('./styles/theme-enterprise.css'); } catch (_) {}
      // UI consistency styles now integrated into main-v501.css (v5.4.1)
    } catch (_) {}

    const validation = Config.validateConfig();
    if (!validation.valid) console.warn('Configuration errors:', validation.errors);
    if (validation.warnings?.length) console.warn('Configuration warnings:', validation.warnings);

    ApiService.initialize?.();
    await LegacyAdapter.initialize();

    // Ensure ActionRouter is installed early (single capture-phase delegator)
    actionRouter.install?.();
    try {
      if (typeof window !== 'undefined') {
        window.__ROUTER_INSTALLED__ = true;
        // Expose router reference for late registrations (ActionRegistry uses this)
        window.__ACTION_ROUTER_REF__ = actionRouter;
      }
      // Safety: ensure router stays installed after initial mount
      setTimeout(() => { try { actionRouter.install?.(); } catch (_) {} }, 0);
    } catch (_) {}

    // Optional prod fallback dispatcher (off by default)
    try {
      if (import.meta.env.PROD && localStorage.getItem('useFallbackDispatcher') === 'true') {
        await import('./utils/FallbackClickDispatcher.js');
      }
    } catch (_) {}
    const app = new App();
    await app.initialize();

    const rootElement = document.getElementById('app');
    if (!rootElement) throw new Error('Root element #app not found');
    await app.mount(rootElement);

    // Apply org branding from control plane if configured
    try {
      const orgId = Config.CONTROL_PLANE?.ORG_ID;
      const base = Config.CONTROL_PLANE?.BASE;
      const workspaceId = Config.CONTROL_PLANE?.WORKSPACE_ID || undefined;
      if (orgId && base) {
        const settings = await ControlPlane.getOrgSettings(orgId);
        const wsSettings = workspaceId ? await ControlPlane.getWorkspaceSettings(workspaceId) : {};
        const primary = settings?.branding?.primaryColor;
        const secondary = settings?.branding?.secondaryColor;
        const logoUrl = settings?.branding?.logoUrl;
        if (primary && typeof document !== 'undefined') {
          document.documentElement.style.setProperty('--color-primary', primary);
          document.documentElement.style.setProperty('--primary-color', primary);
        }
        if (secondary && typeof document !== 'undefined') {
          document.documentElement.style.setProperty('--color-primary-dark', secondary);
          document.documentElement.style.setProperty('--color-accent', secondary);
        }
        if (logoUrl && typeof document !== 'undefined') {
          const host = document.querySelector('.navbar .logo');
          if (host) {
            try {
              // Replace existing SVG with <img>
              host.innerHTML = `<img src="${logoUrl}" alt="Logo" style="height:32px"/> <span>${Config.APP_NAME}</span>`;
            } catch (_) {}
          }
        }

        // Prompt Packs: fetch and merge latest pack (workspace takes precedence)
        try {
          const packs = await ControlPlane.getPromptPacks({ workspaceId });
          const items = Array.isArray(packs?.items) ? packs.items : [];
          // Prefer explicit active pack from workspace/org settings
          const activeId = wsSettings?.promptPacks?.activeId || settings?.promptPacks?.activeId || '';
          const pinnedVersion = wsSettings?.promptPacks?.version || settings?.promptPacks?.version || '';
          let chosen = (activeId && items.find(it => it.id === activeId))
            || items.find(it => it.visibility === 'workspace')
            || items.find(it => it.visibility === 'org')
            || items[0];
          if (chosen && chosen.json_blob && typeof chosen.json_blob === 'object') {
            const blob = chosen.json_blob;
            Object.keys(blob).forEach((key) => {
              const data = blob[key];
              if (data && typeof data === 'object') {
                // Apply via PromptService update
                PromptService.updatePrompt(key, {
                  systemRole: data.systemRole || '',
                  temperature: typeof data.temperature === 'number' ? data.temperature : undefined,
                  customized: true
                });
              }
            });
            // Log loaded pack/version (dev only)
            const loadedInfo = { id: chosen.id, name: chosen.name, version: pinnedVersion || chosen.version, visibility: chosen.visibility };
            if (import.meta.env?.DEV) {
              console.log(`[Prompts] Loaded pack`, loadedInfo);
            }
            try {
              if (import.meta.env?.VITE_PROMPT_PACK_BADGE === 'true') {
                const id = 'prompt-pack-badge';
                if (!document.getElementById(id)) {
                  const el = document.createElement('div');
                  el.id = id;
                  el.style.cssText = 'position:fixed;left:10px;bottom:36px;padding:4px 8px;border-radius:6px;background:rgba(0,94,184,.9);color:#fff;font-size:11px;z-index:2147483647';
                  el.textContent = `Pack: ${loadedInfo.name} v${loadedInfo.version}`;
                  document.body.appendChild(el);
                }
              }
            } catch(_) {}
          }
        } catch(_) {}
      }
    } catch (_) {}

    try { if (typeof window !== 'undefined') window.__STRATEGY_STATIC_STYLES__ = true; } catch (_) {}

    EventBus.emit(AppEvents.APP_READY);
    // Expose App globally for components like TabLayout that reference window.App
    window.App = app;
    try {
      // Install aggregated action maps once modules have rendered their views
      const pendingMaps = (() => { try { return (ActionRegistry.getAll && ActionRegistry.getAll()) || new Map(); } catch(_) { return new Map(); } })();
      ActionRegistry.installAll(actionRouter);
      try {
        const totalNs = pendingMaps.size;
        const totalActions = (actionRouter && actionRouter.handlers && actionRouter.handlers.size) ? actionRouter.handlers.size : undefined;
        if (!totalNs || totalNs === 0) {
          console.warn('[Router] No action maps registered via ActionRegistry');
        }
        if (typeof totalActions === 'number' && totalActions === 0) {
          console.warn('[Router] installAll completed but no handlers are registered');
        }
      } catch (_) {}
      try { if (typeof window !== 'undefined') window.__ACTION_ROUTER_READY__ = true; } catch (_) {}
      try { bindPromptEditors(document.body); } catch (_) {}
      try { EventBus.emit(AppEvents.ROUTER_READY); } catch (_) {}
    } catch (_) {}

    console.log('✅ Application ready!');

    // Add version/environment badge (lower-left)
    try {
      if (!document.getElementById('app-build-badge')) {
        const badge = document.createElement('div');
        badge.id = 'app-build-badge';
        badge.style.cssText = 'position:fixed;left:10px;bottom:10px;padding:6px 10px;border-radius:6px;background:rgba(0,0,0,.6);color:#fff;font-size:12px;z-index:2147483647';
        let sha = '';
        try {
          // Prefer constant injected at build; fallback to env
          // eslint-disable-next-line no-undef
          if (typeof __GIT_SHA__ !== 'undefined' && __GIT_SHA__) sha = String(__GIT_SHA__);
          else if (import.meta.env?.VITE_GIT_SHA) sha = String(import.meta.env.VITE_GIT_SHA);
        } catch (_) {}
        const shortSha = sha ? ` • ${sha.slice(0,7)}` : '';
        badge.textContent = `${Config.APP_NAME} v${Config.APP_VERSION} (${Config.APP_ENV})${shortSha}`;
        document.body.appendChild(badge);
      }
    } catch(_) {}

    // DEV diagnostics stripped for production
    if (false && import.meta.env.DEV) {}

    // UI Health Monitor (DEV only)
    try {
      if (import.meta.env.DEV) {
        const { installUIHealthMonitor } = await import('./utils/HealthMonitor.js');
        installUIHealthMonitor();
      }
    } catch (_) {}
  } catch (error) {
    console.error('❌ Failed to initialize application:', error);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeApp);
} else {
  initializeApp();
}

export { initializeApp };
